// React context provider for runtime feature flags.
//
// Fetches the flag set when the provider mounts, non-blocking: children render
// immediately with flags defaulting to `{}` (all-off), so this never suspends or
// delays first paint. Children render fail-closed until a real 200 lands.
//
// AUTH-READY / TRANSIENT-FAILURE ROBUSTNESS (ENG-1166 fix)
// --------------------------------------------------------
// This provider is mounted by AuthenticatedShell, which renders ONLY once the
// standalone auth shell has reached `authenticated` — so its mount already
// coincides with auth-ready. The failure mode this guards against is subtler:
// the very first `/v1/auth/flags` read can 401 in the brief window after the
// session POST returns but before the `orovia_session` cookie is honoured on a
// following request (qa observed the `[401, 200]` pair). The OLD provider fired
// once and cached WHATEVER settled — including that transient 401 collapsed to
// all-off — permanently, fail-closing an entitled principal out of every AI
// feature across reloads.
//
// The fix: distinguish a real 200 (terminal — authoritative flags, entitled OR
// legitimately-off) from a transient failure (401 pre-auth / 5xx / network) via
// `fetchFlagsResult`, and RETRY transient failures on a bounded backoff. A real
// entitled user converges to enabled once the cookie lands and a 200 arrives; a
// genuinely-unentitled user (200 with flags off) stays correctly disabled with
// no retries. Retries are bounded by `retryDelaysMs` — never an unbounded loop
// or an endpoint hammer.

import { createContext, useEffect, useState, type ReactNode } from 'react'
import { fetchFlagsResult } from './flagsClient'
import type { FlagKey } from './types'

/**
 * Backoff schedule (ms) for retrying a transient flags read. Its LENGTH bounds
 * the number of retries (this many, after the initial attempt), so the endpoint
 * is never hammered. Chosen to comfortably outlast post-login cookie
 * propagation (~6.4s total across 5 attempts) while staying snappy for the
 * common case where the first read already 200s. A module constant so the
 * default reference is stable across renders (safe as a useEffect dep).
 */
export const DEFAULT_FLAGS_RETRY_DELAYS_MS: readonly number[] = [300, 800, 1800, 3500]

export type FlagsStatus = 'loading' | 'ready' | 'error'

export interface FlagsContextValue {
  /** Resolved flag map. Empty until the initial fetch settles (all-off). */
  flags: Record<FlagKey, boolean>
  /** ISO timestamp the backend evaluated the snapshot, or '' before ready. */
  evaluatedAt: string
  /** TTL of the current snapshot in seconds, or 0 before ready. */
  ttlSeconds: number
  /** Lifecycle of the initial fetch. */
  status: FlagsStatus
}

/** `null` when no provider is mounted — consumers then read fail-closed. */
export const FlagsContext = createContext<FlagsContextValue | null>(null)

export interface FlagsProviderProps {
  children: ReactNode
  /**
   * Flag route to read. Defaults to the tenant preview route; the standalone
   * auth shell (ENG-1166) passes `/v1/auth/flags` so AI gates per-principal.
   */
  path?: string
  /**
   * Override the API base. Defaults to `VITE_OROVIA_API_BASE`. The DEV mock
   * harness passes a sentinel base so the injected `fetchImpl` is exercised.
   */
  apiBase?: string
  /** Override the fetch implementation (dev mock harness / tests). */
  fetchImpl?: typeof fetch
  /**
   * Backoff schedule (ms) for retrying a transient flags read; its length bounds
   * the retry count. Defaults to {@link DEFAULT_FLAGS_RETRY_DELAYS_MS}. Exposed
   * for tests (e.g. `[]` for no retries, or short delays under fake timers).
   */
  retryDelaysMs?: readonly number[]
}

const FAIL_CLOSED: FlagsContextValue = {
  flags: {},
  evaluatedAt: '',
  ttlSeconds: 0,
  status: 'error',
}

export function FlagsProvider({
  children,
  path,
  apiBase,
  fetchImpl,
  retryDelaysMs = DEFAULT_FLAGS_RETRY_DELAYS_MS,
}: FlagsProviderProps) {
  const [value, setValue] = useState<FlagsContextValue>({
    flags: {},
    evaluatedAt: '',
    ttlSeconds: 0,
    status: 'loading',
  })

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    // Retry a transient read on the backoff schedule, or — once the schedule is
    // exhausted — settle fail-closed. Bounded by `retryDelaysMs.length`, so
    // there is no unbounded loop and the endpoint is never hammered.
    const scheduleRetryOrFail = (attempt: number) => {
      if (attempt < retryDelaysMs.length) {
        timer = setTimeout(() => {
          if (!cancelled) runAttempt(attempt + 1)
        }, retryDelaysMs[attempt])
        return
      }
      setValue(FAIL_CLOSED)
    }

    // `attempt` is 0-based: 0 is the initial read, 1..N the scheduled retries.
    const runAttempt = (attempt: number) => {
      // fetchFlagsResult is fail-closed and never rejects; the .catch is purely
      // defensive against a future change and is treated as a transient failure.
      fetchFlagsResult({ path, apiBase, fetchImpl })
        .then((result) => {
          if (cancelled) return
          if (result.outcome === 'ok' || result.outcome === 'unconfigured') {
            // Terminal: authoritative flags (entitled → enabled; unentitled →
            // legitimately off) or no backend configured. Stop — no retries.
            setValue({
              flags: result.response.flags,
              evaluatedAt: result.response.evaluatedAt,
              ttlSeconds: result.response.ttlSeconds,
              status: 'ready',
            })
            return
          }
          // Transient (401 pre-auth / 5xx / network / unparseable): keep the
          // fail-closed loading state and retry rather than caching "all off".
          scheduleRetryOrFail(attempt)
        })
        .catch(() => {
          if (cancelled) return
          scheduleRetryOrFail(attempt)
        })
    }

    runAttempt(0)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [path, apiBase, fetchImpl, retryDelaysMs])

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>
}
